
/**
 * Utility functions for form handling, particularly for comparing nested form data
 */

/**
 * Deeply compares two objects to determine if they are different
 * Works with any level of nesting
 */
export const areObjectsDifferent = (obj1: any, obj2: any): boolean => {
  // Handle primitive types and null/undefined
  if (obj1 === obj2) return false;
  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) return true;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  // Different number of properties
  if (keys1.length !== keys2.length) return true;
  
  // Compare each property recursively
  for (const key of keys1) {
    // Key doesn't exist in other object
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return true;
    
    // Special handling for primitive values to ensure more reliable comparison
    if (typeof obj1[key] !== 'object' || obj1[key] === null) {
      if (obj1[key] !== obj2[key]) return true;
      continue;
    }
    
    // Recursively compare nested objects
    if (areObjectsDifferent(obj1[key], obj2[key])) return true;
  }
  
  return false;
};

/**
 * Specifically for comparing form values to detect changes
 * Handles react-hook-form values with nested structures
 * Uses both stringification and field-by-field comparison for maximum reliability
 */
export const haveFormValuesChanged = (currentValues: any, initialValues: any): boolean => {
  if (!initialValues || !currentValues) return false;
  
  try {
    // First attempt: Direct field-by-field comparison for critical fields
    // This ensures we catch changes to important fields like "name" even if deeper comparison fails
    const criticalFields = ['name', 'visibility'];
    for (const field of criticalFields) {
      if (currentValues[field] !== initialValues[field]) {
        console.log(`Critical field "${field}" changed: "${initialValues[field]}" → "${currentValues[field]}"`);
        return true;
      }
    }
    
    // Second attempt: JSON stringify for reliable comparison of complex objects
    const currentStr = JSON.stringify(sortObjectKeys(currentValues));
    const initialStr = JSON.stringify(sortObjectKeys(initialValues));
    
    const stringsMatch = currentStr === initialStr;
    if (!stringsMatch) {
      console.log("String comparison detected changes between form values");
      
      // For debugging, show which fields changed
      const differences = getDifferences(currentValues, initialValues);
      if (differences.length > 0) {
        console.log("Changed fields:", differences);
      }
    }
    
    return !stringsMatch;
  } catch (e) {
    console.error("Error in form value comparison:", e);
    // Fallback to object comparison if stringification fails
    return areObjectsDifferent(currentValues, initialValues);
  }
};

/**
 * Sort object keys for consistent stringification comparison
 */
export const sortObjectKeys = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  
  return Object.keys(obj)
    .sort()
    .reduce((sorted: any, key: string) => {
      sorted[key] = typeof obj[key] === 'object' && obj[key] !== null
        ? sortObjectKeys(obj[key])
        : obj[key];
      return sorted;
    }, {});
};

/**
 * Creates a deep clone of an object to avoid reference issues
 * Uses structured clone for best performance and reliability
 */
export const deepClone = <T>(obj: T): T => {
  if (!obj) return obj;
  try {
    // Try the most reliable method first
    if (typeof structuredClone === 'function') {
      return structuredClone(obj);
    }
    
    // Fallback to JSON method
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    console.error("Error in deep cloning:", e);
    // Last resort fallback
    return { ...obj } as T;
  }
};

/**
 * Logs the specific differences between objects (useful for debugging)
 * Enhanced with clearer output and value comparison
 */
export const logObjectDifferences = (obj1: any, obj2: any, label = 'Objects'): void => {
  console.log(`Comparing ${label}:`, {
    obj1Size: JSON.stringify(obj1).length,
    obj2Size: JSON.stringify(obj2).length,
    areEqual: JSON.stringify(sortObjectKeys(obj1)) === JSON.stringify(sortObjectKeys(obj2))
  });
  
  try {
    const differences = getDifferences(obj1, obj2);
    if (differences.length > 0) {
      console.log(`Differences in ${label} (${differences.length} changes):`);
      differences.forEach(diff => {
        console.log(`  - ${diff.path}: "${diff.oldValue}" → "${diff.newValue}"`);
      });
    } else {
      console.log(`No differences found in ${label} despite string comparison difference`);
    }
  } catch (e) {
    console.error(`Error detecting differences in ${label}:`, e);
  }
};

/**
 * Returns an array of paths where objects differ
 * Enhanced to properly handle edge cases and provide clearer output
 */
const getDifferences = (current: any, initial: any, path = ""): Array<{path: string, oldValue: any, newValue: any}> => {
  if (!initial || !current) return [];
  
  const differences: Array<{path: string, oldValue: any, newValue: any}> = [];
  const allKeys = new Set([...Object.keys(current), ...Object.keys(initial)]);
  
  allKeys.forEach(key => {
    const currentPath = path ? `${path}.${key}` : key;
    
    // Skip non-data fields that are expected to change
    if (['id', 'createdAt', 'lastEditedAt'].includes(key)) {
      return;
    }
    
    if (!(key in current)) {
      differences.push({
        path: currentPath,
        oldValue: initial[key],
        newValue: undefined
      });
      return;
    }
    
    if (!(key in initial)) {
      differences.push({
        path: currentPath,
        oldValue: undefined,
        newValue: current[key]
      });
      return;
    }
    
    const currentVal = current[key];
    const initialVal = initial[key];
    
    // Special handling for primitive values
    if (typeof currentVal !== 'object' || currentVal === null || 
        typeof initialVal !== 'object' || initialVal === null) {
      if (currentVal !== initialVal) {
        differences.push({
          path: currentPath,
          oldValue: initialVal,
          newValue: currentVal
        });
      }
      return;
    }
    
    // Handle Firestore references and other special objects
    if (isSpecialReference(currentVal) && isSpecialReference(initialVal)) {
      // Compare by ID for reference objects
      if (currentVal.id !== initialVal.id) {
        differences.push({
          path: currentPath,
          oldValue: initialVal.id || initialVal,
          newValue: currentVal.id || currentVal
        });
      }
      return;
    }
    
    // Add depth limit
    if (currentPath.split('.').length > 10) {
      if (JSON.stringify(currentVal) !== JSON.stringify(initialVal)) {
        differences.push({
          path: currentPath,
          oldValue: summarizeValue(initialVal),
          newValue: summarizeValue(currentVal)
        });
      }
      return;
    }
    
    // Recursively compare nested objects
    const nestedDiffs = getDifferences(currentVal, initialVal, currentPath);
    differences.push(...nestedDiffs);
  });
  
  return differences;
};

/**
 * Check if an object is a special reference type (like Firestore reference)
 */
const isSpecialReference = (obj: any): boolean => {
  return obj && typeof obj === 'object' && 
    (('path' in obj && 'id' in obj) || // Firestore reference
     ('_key' in obj && '_firestore' in obj)); // Another reference type
};

/**
 * Summarize a value for display in logs (handles circular references)
 */
const summarizeValue = (val: any): string => {
  if (val === undefined) return 'undefined';
  if (val === null) return 'null';
  
  try {
    if (typeof val === 'object') {
      if (isSpecialReference(val)) {
        return `[Ref: ${val.id || JSON.stringify(val).substring(0, 20)}]`;
      }
      return JSON.stringify(val).substring(0, 50) + (JSON.stringify(val).length > 50 ? '...' : '');
    }
    return String(val);
  } catch (e) {
    return '[Complex Value]';
  }
};

/**
 * Track changes at the field level by creating a change map
 */
export const createFieldChangeMap = (
  currentValues: any, 
  initialValues: any
): Record<string, { changed: boolean, oldValue: any, newValue: any }> => {
  const changeMap: Record<string, { changed: boolean, oldValue: any, newValue: any }> = {};
  const differences = getDifferences(currentValues, initialValues);
  
  // Create a flat map of all changed fields
  differences.forEach(diff => {
    const fieldPath = diff.path;
    changeMap[fieldPath] = {
      changed: true,
      oldValue: diff.oldValue,
      newValue: diff.newValue
    };
  });
  
  return changeMap;
};
